import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { ArrowLeft, UserPlus, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import branding from '@/config/branding';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Investment amount options in Lakhs
const INVESTMENT_AMOUNTS = [
  { label: '1-2 Lakhs', value: '1-2' },
  { label: '3-5 Lakhs', value: '3-5' },
  { label: '6-9 Lakhs', value: '6-9' },
  { label: '10-15 Lakhs', value: '10-15' },
  { label: '16-25 Lakhs', value: '16-25' },
  { label: '25+ Lakhs', value: '25+' },
];

export default function SignupPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [accountExistsError, setAccountExistsError] = useState(null);
  
  // New investment preference fields
  const [investmentAmounts, setInvestmentAmounts] = useState([]);
  const [wantsSIP, setWantsSIP] = useState(null); // null, 'yes', 'no'
  const [sipFrequency, setSipFrequency] = useState(null); // 'monthly', 'quarterly'
  const [sipAmount, setSipAmount] = useState('');
  const [sipAmountError, setSipAmountError] = useState('');

  // Handle investment amount checkbox toggle
  const handleInvestmentAmountToggle = (value) => {
    setInvestmentAmounts(prev => 
      prev.includes(value) 
        ? prev.filter(v => v !== value)
        : [...prev, value]
    );
  };

  // Minimum SIP amount for both Monthly and Quarterly
  const MIN_SIP_AMOUNT = 50001;

  // Validate SIP amount
  const validateSipAmount = (amount) => {
    if (amount && parseInt(amount) < MIN_SIP_AMOUNT) {
      setSipAmountError('Amount is below the minimum limit');
      return false;
    }
    setSipAmountError('');
    return true;
  };

  const handleSipAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, ''); // Only allow numbers
    setSipAmount(value);
    if (value) {
      validateSipAmount(value);
    } else {
      setSipAmountError('');
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setAccountExistsError(null); // Clear any previous error
    
    if (!email || !password || !phone || !firstName || !lastName) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (!termsAccepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    // Validate investment preferences
    if (investmentAmounts.length === 0) {
      toast.error('Please select at least one investment amount range');
      return;
    }

    if (wantsSIP === null) {
      toast.error('Please indicate if you want to invest in SIP structure');
      return;
    }

    if (wantsSIP === 'yes') {
      if (!sipFrequency) {
        toast.error('Please select SIP frequency (Monthly or Quarterly)');
        return;
      }
      if (!sipAmount) {
        toast.error('Please enter SIP amount');
        return;
      }
      if (parseInt(sipAmount) < MIN_SIP_AMOUNT) {
        toast.error('SIP amount is below the minimum limit');
        return;
      }
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/signup`, {
        email,
        password,
        phone,
        first_name: firstName,
        last_name: lastName,
        capital_deployment: 0,
        vehicle: '',
        terms_accepted: termsAccepted,
        // New investment preference fields
        investment_amounts: investmentAmounts,
        wants_sip: wantsSIP === 'yes',
        sip_frequency: wantsSIP === 'yes' ? sipFrequency : null,
        sip_amount: wantsSIP === 'yes' && sipAmount ? parseInt(sipAmount) : null
      }, { withCredentials: true });
      
      const { user, session_token } = response.data;
      
      localStorage.setItem('user_email', user.email);
      localStorage.setItem('user_status', user.status);
      localStorage.setItem('session_token', session_token);
      
      toast.success('Account created! Your access is pending approval.');
      navigate('/pending');
    } catch (error) {
      const errorCode = error.response?.data?.detail;
      
      if (errorCode === 'EMAIL_EXISTS') {
        setAccountExistsError('email');
      } else if (errorCode === 'PHONE_EXISTS') {
        setAccountExistsError('phone');
      } else {
        const message = errorCode || 'Signup failed';
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-4 text-[#64748B] hover:text-[#0F172A] px-0"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <Card className="border-[#E2E8F0] shadow-none">
          <CardHeader className="space-y-1">
            <div className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-brand" />
              <CardTitle className="font-heading text-xl font-bold text-[#0F172A]">
                CREATE ACCOUNT
              </CardTitle>
            </div>
            <CardDescription className="text-[#64748B] text-sm">
              Fill in your details to join {branding.platformName}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <form onSubmit={handleSignup} className="space-y-4">
                {/* Account exists error message */}
                {accountExistsError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                    <p className="text-red-700 text-sm">
                      {accountExistsError === 'email' 
                        ? 'An account with this email already exists.' 
                        : 'An account with this phone number already exists.'}
                    </p>
                    <p className="text-red-600 text-sm mt-2">
                      Please{' '}
                      <Link 
                        to="/login" 
                        className="text-brand font-semibold underline hover:text-brand-hover"
                      >
                        sign in
                      </Link>
                      {' '}instead.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#0F172A] font-medium text-sm">First Name *</Label>
                    <Input
                      placeholder="Enter your first name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="bg-white border-[#E2E8F0] h-10 text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#0F172A] font-medium text-sm">Last Name *</Label>
                    <Input
                      placeholder="Enter your last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="bg-white border-[#E2E8F0] h-10 text-sm"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#0F172A] font-medium text-sm">Email *</Label>
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-white border-[#E2E8F0] h-10 text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#0F172A] font-medium text-sm">Phone Number *</Label>
                  <Input
                    type="tel"
                    placeholder="Enter your phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="bg-white border-[#E2E8F0] h-10 text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#0F172A] font-medium text-sm">Password *</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Create a password (min 6 characters)"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-white border-[#E2E8F0] h-10 text-sm pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#0F172A]"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#0F172A] font-medium text-sm">Confirm Password *</Label>
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`bg-white border-[#E2E8F0] h-10 text-sm ${confirmPassword && password !== confirmPassword ? 'border-red-500' : ''}`}
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-red-500 text-xs">Passwords do not match</p>
                  )}
                </div>

                {/* Investment Amount Selection */}
                <div className="space-y-3 pt-2">
                  <Label className="text-[#0F172A] font-medium text-sm">
                    How much would you like to invest per startup? *
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    {INVESTMENT_AMOUNTS.map((option) => (
                      <div 
                        key={option.value} 
                        className="flex items-center space-x-2"
                      >
                        <Checkbox
                          id={`investment-${option.value}`}
                          checked={investmentAmounts.includes(option.value)}
                          onCheckedChange={() => handleInvestmentAmountToggle(option.value)}
                          className="border-[#E2E8F0] data-[state=checked]:bg-brand"
                        />
                        <label 
                          htmlFor={`investment-${option.value}`} 
                          className="text-sm text-[#0F172A] cursor-pointer"
                        >
                          {option.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* SIP Investment Question */}
                <div className="space-y-3 pt-2">
                  <Label className="text-[#0F172A] font-medium text-sm">
                    Do you also want to invest in Startup in SIP structure? *
                  </Label>
                  <RadioGroup 
                    value={wantsSIP || ''} 
                    onValueChange={(value) => {
                      setWantsSIP(value);
                      if (value === 'no') {
                        setSipFrequency(null);
                        setSipAmount('');
                        setSipAmountError('');
                      }
                    }}
                    className="flex gap-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="sip-yes" className="border-[#E2E8F0] text-brand" />
                      <label htmlFor="sip-yes" className="text-sm text-[#0F172A] cursor-pointer">Yes</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="sip-no" className="border-[#E2E8F0] text-brand" />
                      <label htmlFor="sip-no" className="text-sm text-[#0F172A] cursor-pointer">No</label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Conditional SIP Fields - Only show if "Yes" is selected */}
                {wantsSIP === 'yes' && (
                  <>
                    {/* SIP Frequency */}
                    <div className="space-y-3 pt-2 pl-4 border-l-2 border-brand/20">
                      <Label className="text-[#0F172A] font-medium text-sm">
                        SIP Frequency *
                      </Label>
                      <RadioGroup 
                        value={sipFrequency || ''} 
                        onValueChange={(value) => {
                          setSipFrequency(value);
                          // Re-validate amount when frequency changes
                          if (sipAmount) {
                            validateSipAmount(sipAmount);
                          }
                        }}
                        className="flex gap-6"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="monthly" id="freq-monthly" className="border-[#E2E8F0] text-brand" />
                          <label htmlFor="freq-monthly" className="text-sm text-[#0F172A] cursor-pointer">Monthly</label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="quarterly" id="freq-quarterly" className="border-[#E2E8F0] text-brand" />
                          <label htmlFor="freq-quarterly" className="text-sm text-[#0F172A] cursor-pointer">Quarterly</label>
                        </div>
                      </RadioGroup>
                    </div>

                    {/* SIP Amount */}
                    <div className="space-y-2 pl-4 border-l-2 border-brand/20">
                      <Label className="text-[#0F172A] font-medium text-sm">
                        SIP Amount (INR) *
                      </Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        placeholder="Enter amount"
                        value={sipAmount}
                        onChange={handleSipAmountChange}
                        className={`bg-white border-[#E2E8F0] h-10 text-sm ${sipAmountError ? 'border-red-500' : ''}`}
                      />
                      {sipAmountError && (
                        <p className="text-red-500 text-xs">{sipAmountError}</p>
                      )}
                      {sipAmount && !sipAmountError && (
                        <p className="text-[#64748B] text-xs">
                          ₹{parseInt(sipAmount).toLocaleString('en-IN')}
                        </p>
                      )}
                    </div>
                  </>
                )}

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="terms"
                    checked={termsAccepted}
                    onCheckedChange={setTermsAccepted}
                    className="border-[#E2E8F0] data-[state=checked]:bg-brand"
                  />
                  <label htmlFor="terms" className="text-xs text-[#64748B]">
                    I accept the terms and conditions of {branding.platformName}
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-brand hover:bg-brand-hover text-white h-12 font-semibold rounded-md text-base"
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </form>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-4">
          <button
            onClick={() => navigate('/login')}
            className="text-sm text-[#64748B] hover:text-[#0F172A]"
          >
            Already have an account? <span className="font-semibold text-brand">Login</span>
          </button>
        </div>
      </div>
    </div>
  );
}
